import React, { useEffect, useState } from "react";
import RoleService from "../../../services/KangarooServices/RoleService";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AddRole from "../Component/AddRole";
import RoleDetails from "./RoleDetails";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";

const Role = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const navigate = useNavigate();
  const toggleDetailModal = (id) => {
    setSelectedRoleId(id);
    setModalOpen(!modalOpen);
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const toggleModal = (id = null) => {
    setSelectedRoleId(id);
    setIsOpen(!isOpen);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await RoleService.getList();
      setData(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await RoleService.deleteRole(selectedDeleteItem.id);
        await fetchData();
        toast.success("Role deleted successfully");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to delete Role");
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.9rem",
        fontWeight: 600,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const filteredData = data.filter(
    (item) =>
      (item.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.description || "").toLowerCase().includes(filterText.toLowerCase()),
  );

  const columns = [
    {
      name: "S. N",
      cell: (row, index) => (
        <div>{(currentPage - 1) * rowsPerPage + index + 1}</div>
      ),
      width: "75px",

      center: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
      wrap: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            color="warning"
            className="btn-soft-warning "
            onClick={() => navigate(`/roles/update/${row?.id}`)}
          >
            <i className="ri-pencil-line"></i>
          </Button>

          <Button
            size="sm"
            color="info"
            className="btn-soft-info "
            onClick={() => toggleDetailModal(row.id)}
          >
            <i className="ri-eye-line"></i>
          </Button>

          <Button
            size="sm"
            color="danger"
            className="btn-soft-danger "
            onClick={() => handleDeleteClick(row)}
          >
            <i className="ri-delete-bin-5-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Roles" pageTitle="User Management" />

        <Row className="mb-4">
          <Col lg={6} xs={12} className="mb-3 mb-lg-0">
            <Input
              type="text"
              placeholder="Search by name or description..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{
                borderRadius: "8px",
                padding: "12px 16px",
              }}
            />
          </Col>
          <Col
            lg={6}
            xs={12}
            className="d-flex justify-content-end align-items-end "
          >
            <Button
              className="btn btn-success"
              onClick={() => navigate("/roles/create")}
            >
              <i className="ri-add-line align-bottom me-1"></i>
              Add Role
            </Button>
          </Col>
        </Row>

        {/* Desktop Table */}
        <Card className="d-none d-lg-block">
          <CardBody>
            <CustomDataTable
              columns={columns}
              data={filteredData}
              customStyles={customStyles}
              pagination
              striped
              responsive
              fixedHeader
              progressPending={loading}
              paginationPerPage={rowsPerPage}
              onChangeRowsPerPage={(newPerPage) => setRowsPerPage(newPerPage)}
              onChangePage={(page) => setCurrentPage(page)}
              progressComponent={
                <div className="my-3">
                  <Triangle
                    visible={true}
                    height="80"
                    width="80"
                    color="#5B71B9"
                    ariaLabel="triangle-loading"
                  />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
            />
          </CardBody>
        </Card>

        {/* Mobile Cards */}
        <div className="d-block d-lg-none">
          {/* Floating Add Button for Mobile */}
          <button
            className="btn btn-success"
            onClick={() => toggleModal()}
            style={{
              position: "fixed",
              bottom: "24px",
              right: "24px",
              borderRadius: "50%",
              width: "56px",
              height: "56px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              zIndex: 1000,
              border: "none",
            }}
          >
            <i className="ri-add-line"></i>
          </button>

          {loading ? (
            <div className="d-flex justify-content-center my-5">
              <Triangle visible={true} height="80" width="80" color="#5B71B9" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="ri-shield-user-line"
                style={{ fontSize: "48px", color: "#999" }}
              ></i>
              <p className="text-muted mt-3">No roles found</p>
            </div>
          ) : (
            <div style={{ paddingBottom: "80px" }}>
              {filteredData.map((role, index) => (
                <Card
                  key={role.id}
                  className="mb-3 border-0 border-start border-4 border-primary"
                  style={{
                    borderRadius: "12px",
                    border: "1px solid #e8e8e8",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    overflow: "visible",
                    transition: "all 0.2s ease",
                    position: "relative",
                  }}
                >
                  <CardBody style={{ padding: "16px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h6
                          className="mb-2"
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#2c3e50",
                            lineHeight: "1.3",
                          }}
                        >
                          {role.name}
                        </h6>

                        <p
                          className="mb-0"
                          style={{
                            fontSize: "14px",
                            color: "#7f8c8d",
                            lineHeight: "1.5",
                          }}
                        >
                          {role.description}
                        </p>
                      </div>

                      <Dropdown
                        isOpen={role.dropdownOpen || false}
                        toggle={() => {
                          setData((prev) =>
                            prev.map((r) =>
                              r.id === role.id
                                ? { ...r, dropdownOpen: !r.dropdownOpen }
                                : { ...r, dropdownOpen: false },
                            ),
                          );
                        }}
                        direction="down"
                      >
                        <DropdownToggle
                          color="light"
                          className="btn-sm shadow-none"
                          style={{
                            borderRadius: "8px",
                            padding: "8px 12px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <i
                            className="ri-more-2-fill"
                            style={{ fontSize: "16px", color: "#495057" }}
                          ></i>
                        </DropdownToggle>
                        <DropdownMenu
                          end
                          style={{
                            minWidth: "140px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            border: "1px solid #e8e8e8",
                            borderRadius: "8px",
                            fontSize: "0.875rem",
                            marginTop: "4px",
                          }}
                          modifiers={[
                            {
                              name: "offset",
                              options: {
                                offset: [0, 4],
                              },
                            },
                          ]}
                        >
                          <DropdownItem
                            onClick={() => toggleModal(role.id)}
                            style={{
                              padding: "8px 12px",
                              fontSize: "0.875rem",
                            }}
                          >
                            <i
                              className="ri-pencil-line text-warning me-2"
                              style={{ fontSize: "14px" }}
                            ></i>{" "}
                            Edit
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => toggleDetailModal(role.id)}
                            style={{
                              padding: "8px 12px",
                              fontSize: "0.875rem",
                            }}
                          >
                            <i
                              className="ri-eye-line text-info me-2"
                              style={{ fontSize: "14px" }}
                            ></i>{" "}
                            View
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => handleDeleteClick(role)}
                            style={{
                              padding: "8px 12px",
                              fontSize: "0.875rem",
                            }}
                          >
                            <i
                              className="ri-delete-bin-5-line text-danger me-2"
                              style={{ fontSize: "14px" }}
                            ></i>{" "}
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* <AddRole
          isOpen={isOpen}
          toggle={toggleModal}
          fetchData={fetchData}
          id={selectedRoleId}
        /> */}

        <RoleDetails
          isOpen={modalOpen}
          toggle={toggleDetailModal}
          id={selectedRoleId}
        />

        <DeleteModal
          show={isOpenDelete}
          onCloseClick={toggleDelete}
          onDeleteClick={handleDelete}
        />
      </Container>
    </div>
  );
};

export default Role;
